import { authorizedFetch } from '../../helpers/authorizedFetch'
import { formatDate } from '../../helpers/date'
import trans from '../../helpers/trans'
import { generateApiUrl } from '../../helpers/url'
import isResourceCollectionDocument from '../../japi/guards/isResourceCollectionDocument'
import isResourceDocument from '../../japi/guards/isResourceDocument'
import { JapiDocument } from '../../japi/types/Document'
import { DateFormat } from '../../types/dateFormatTypes'
import { FetchCollectionResult, FetchResult, FetchResultType } from '../FetchResult'
import { NotificationCenterHeaderName, NotificationCenterLog } from '../NotificationCenter/NotificationCenter'
import { Department, DepartmentFormData, DepartmentResource } from './Department'
import { getDepartmentFromApiData } from './DepartmentTransformers'

export const getDepartmentsIndexCall = async (): Promise<FetchCollectionResult<Department[], string>> => {
    try {
        const url = generateApiUrl({
            endpoint: '/departments',
            queryParams: {
                include: 'group',
            },
        })

        const response = await authorizedFetch(url)

        const doc: JapiDocument = await response.json()

        if (! isResourceCollectionDocument<DepartmentResource>(doc)) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const departments = doc.data.map(departmentResource => getDepartmentFromApiData(doc, departmentResource))

        return {
            amountOfPages: 1,
            status: response.status,
            type: FetchResultType.Success,
            data: departments,
        }
    } catch (error) {
        console.error('[getDepartmentsIndexCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const postDepartmentApiCall = async (
    data: DepartmentFormData,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<Department, string>> => {
    try {
        const body = {
            data: {
                type: 'departments',
                attributes: {
                    name: data.name,
                },
                ...(data.groupId ? { relationships: {
                    group: {
                        data: data.groupId ? {
                            type: 'groups',
                            id: data.groupId,
                        } : null,
                    },
                } } : undefined),
            },
        }

        const url = generateApiUrl({
            endpoint: '/departments',
            queryParams: {
                include: 'group',
            },
        })

        const response = await authorizedFetch(url, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<DepartmentResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            }
        }

        const department = getDepartmentFromApiData(doc, doc.data)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: department,
        }
    } catch (error) {
        console.error('[postDepartmentApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const patchDepartmentApiCall = async (
    data: Partial<DepartmentFormData>,
    departmentId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<Department, string>> => {
    try {
        const body = {
            data: {
                type: 'departments',
                id: departmentId,
                attributes: {
                    name: data.name,
                },
                relationships: {
                    group: {
                        data: data.groupId ? {
                            type: 'groups',
                            id: data.groupId,
                        } : null,
                    },
                },
            },
        }

        const url = generateApiUrl({
            endpoint: `/departments/${departmentId}`,
            queryParams: {
                include: 'group',
            },
        })

        const response = await authorizedFetch(url, {
            method: 'PATCH',
            body: JSON.stringify(body),
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<DepartmentResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            }
        }

        const department = getDepartmentFromApiData(doc, doc.data)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: department,
        }
    } catch (error) {
        console.error('[patchDepartmentApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const patchSoftDeleteDepartmentApiCall = async (
    departmentId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<Department, string>> => {
    try {
        const url = generateApiUrl({
            endpoint: `/departments/${departmentId}`,
            queryParams: {
                include: 'group',
            },
        })

        const response = await authorizedFetch(url, {
            method: 'PATCH',
            body: JSON.stringify({
                data: {
                    type: 'departments',
                    id: departmentId,
                    attributes: {
                        deletedAt: formatDate(new Date(), DateFormat.apiDateTime),
                    },
                },
            }),
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<DepartmentResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            }
        }

        const department = getDepartmentFromApiData(doc, doc.data)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: department,
        }
    } catch (error) {
        console.error('[patchSoftDeleteDepartmentApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}
